import { CategoryType, CategoryTypeLabels } from '../enums/category-type';
import { IPatientReport } from 'src/app/pages/dialogs/patient-report-print/ipatient-report';

export interface GroupedCategory {
  category: string;
  subCategories: {
    subCategory: string;
    items: IPatientReport[];
  }[];
}

export interface GroupedReportSection {
  categoryType: string;
  categories: GroupedCategory[];
}

export function groupPatientReports(reports: IPatientReport[]): GroupedReportSection[] {
  const categoryTypeMap = new Map<string, Map<string, Map<string, IPatientReport[]>>>();

  for (const report of reports || []) {
    const categoryType = report.categoryTypeName || '';
    const category = report.categoryName || '';
    const subCategory = report.subCategoryName || '';

    if (!categoryTypeMap.has(categoryType)) {
      categoryTypeMap.set(categoryType, new Map<string, Map<string, IPatientReport[]>>());
    }
    const categoryMap = categoryTypeMap.get(categoryType)!;

    if (!categoryMap.has(category)) {
      categoryMap.set(category, new Map<string, IPatientReport[]>());
    }
    const subCategoryMap = categoryMap.get(category)!;

    if (!subCategoryMap.has(subCategory)) {
      subCategoryMap.set(subCategory, []);
    }
    subCategoryMap.get(subCategory)!.push(report);
  }

  const displayOrder: CategoryType[] = [
    CategoryType.CaseHistory,
    CategoryType.ClinicalExam,
    CategoryType.SuggestedInvestigation,
    CategoryType.Diagnosis,
  ];

  const normalize = (value: string) => value.replace(/\s+/g, '').toLowerCase();

  const getOrderIndex = (categoryTypeName: string): number => {
    const match = Object.entries(CategoryTypeLabels).find(
      ([, label]) => normalize(String(label)) === normalize(categoryTypeName)
    );
    const enumValue = match ? Number(match[0]) : null;
    const index = displayOrder.indexOf(enumValue as CategoryType);
    return index === -1 ? 999 : index;
  };

  return Array.from(categoryTypeMap, ([categoryType, categoryMap]) => ({
    categoryType,
    categories: Array.from(categoryMap, ([category, subCategoryMap]) => ({
      category,
      subCategories: Array.from(subCategoryMap, ([subCategory, items]) => ({ subCategory, items }))
    }))
  })).sort((a, b) => getOrderIndex(a.categoryType) - getOrderIndex(b.categoryType));
}

export function buildClinicalSectionsPrintHtml(groupedReports: GroupedReportSection[], showCategory: boolean = true): string {
  if (!groupedReports || groupedReports.length === 0) {
    return '';
  }

  return groupedReports.map(group => {
    const isDiagnosis = group.categoryType.toLowerCase().includes('diagnosis');

    const categoriesHtml = group.categories.map(cat => {
      const subRowsHtml = cat.subCategories.map(sub => {
        const itemsHtml = sub.items.map(item => {
          const duration = item.duration ? ` (${escapeHtml(item.duration)})` : '';
          const note = (isDiagnosis && item.note) ? ` - ${escapeHtml(item.note)}` : '';
          return `<li><span>${escapeHtml(item.clinicalItemName)}</span>${duration}${note}</li>`;
        }).join('');

        const subCategoryCol = showCategory 
          ? `<div class="middle-col" style="font-weight:600;">${escapeHtml(sub.subCategory)}</div>` 
          : '';

        return `
          <div class="report-sub-row ${showCategory ? '' : 'report-sub-row-no-category'}">
            ${subCategoryCol}
            <div class="right-col">
              <ul class="finding-list">${itemsHtml}</ul>
            </div>
          </div>
        `;
      }).join('');

      const categoryCol = showCategory 
        ? `<div class="left-col category-col" style="font-weight:600;">${escapeHtml(cat.category)}</div>` 
        : '';

      return `
        <div class="report-category ${showCategory ? '' : 'report-category-no-category'}">
          ${categoryCol}
          <div class="subcategory-list">
            ${subRowsHtml}
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="report-section">
        <div class="report-section-header">
          <h4 class="category-type" style="margin:0 0 6px 0; font-size:14px; font-weight:700;">${escapeHtml(group.categoryType)}</h4>
        </div>
        <div class="report-grid ${isDiagnosis ? 'diagnosis-grid' : ''}">
          ${categoriesHtml}
        </div>
      </div>
    `;
  }).join('');
}

export function getSharedReportPrintStyles(primaryColor: string): string {
  return `
    .dynamic-section {
      color: #0f1f44;
      font-size: 12px;
    }

    .report-section {
      margin-bottom: 16px;
      break-inside: auto;
      page-break-inside: auto;
    }

    .report-section-header {
      border-bottom: 0;
      margin-bottom: 4px;
      padding-bottom: 0;
    }

    .category-type,
    .diagnosis-section {
      color: ${primaryColor};
    }

    .report-grid {
      overflow: visible;
      border: 1px solid color-mix(in srgb, ${primaryColor} 22%, #d7deea 78%);
      border-radius: 3px;
      background: #fff;
    }

    .report-category {
      display: grid;
      grid-template-columns: minmax(120px, 18%) 1fr;
      min-height: 36px;
      border-bottom: 1px solid color-mix(in srgb, ${primaryColor} 22%, #d7deea 78%);
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .report-category:last-child {
      border-bottom: 0;
    }

    .report-category-no-category {
      grid-template-columns: 1fr;
    }

    .subcategory-list {
      min-width: 0;
    }

    .report-sub-row {
      display: grid;
      grid-template-columns: minmax(160px, 30%) 1fr;
      min-height: 36px;
      border-bottom: 1px solid color-mix(in srgb, ${primaryColor} 22%, #d7deea 78%);
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .report-sub-row:last-child {
      border-bottom: 0;
    }

    .report-sub-row-no-category {
      grid-template-columns: 1fr;
    }

    .left-col,
    .middle-col,
    .right-col {
      min-height: 36px;
      padding: 6px 10px;
      color: #0f1f44;
      font-size: 12px;
    }

    .left-col,
    .middle-col {
      border-right: 1px solid color-mix(in srgb, ${primaryColor} 22%, #d7deea 78%);
    }

    .category-col {
      align-self: stretch;
    }

    .middle-col {
      color: #0f1f44;
      font-weight: 500;
    }

    .finding-list {
      margin: 0;
      padding-left: 15px;
    }

    .finding-list li {
      margin-bottom: 2px;
    }

    .diagnosis-grid .report-sub-row {
      min-height: 50px;
    }

    .treatment-advice-section {
      margin: 16px 0;
      break-inside: auto;
      page-break-inside: auto;
    }

    .treatment-advice-header {
      margin-bottom: 6px;
    }

    .treatment-advice-title {
      font-size: 14px;
      font-weight: 700;
      color: ${primaryColor};
      margin: 0 0 6px 0;
    }

    .treatment-advice-content {
      padding: 8px 14px;
      border: 1px solid color-mix(in srgb, ${primaryColor} 22%, #d7deea 78%);
      border-radius: 4px;
      color: #0f1f44;
      font-size: 12.5px;
      line-height: 1.6;
      white-space: pre-wrap;
      background: #fff;
    }
  `;
}

function escapeHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
